/**
 * Like/Favorite Cell Component
 * 
 * Interactive heart icon that toggles like status for a document.
 * Uses local state for optimistic updates (same pattern as detail page sidebar).
 */

import { useState, useEffect, useMemo } from "react";
import { Heart } from "lucide-react";
import { useToggleLikeInList } from "../api/useToggleLikeInList";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { parseLikedBy } from "../utils/parseLikedBy";
import { getCandidateLikeUserIds } from "../utils/likeUserIds";

type LikeCellProps = {
  doctype: string;
  name: string;
  likedByRaw?: string | string[] | null;
};

export function LikeCell({ doctype, name, likedByRaw }: LikeCellProps) {
  const { data: currentUser } = useCurrentUser();
  const likedByArray = parseLikedBy(likedByRaw);

  // Frappe stores `_liked_by` as a list of user identifiers (often email, sometimes username).
  // Our auth payload may provide either/both. Consider all plausible identifiers to avoid false negatives.
  const candidateUserIds = useMemo(() => {
    return getCandidateLikeUserIds(currentUser);
  }, [currentUser?.email, currentUser?.name]);
  
  // Calculate initial liked state from props (same as detail page)
  const initiallyLiked = useMemo(
    () => candidateUserIds.length > 0 && likedByArray.some((u) => candidateUserIds.includes(u)),
    [likedByArray, candidateUserIds]
  );

  // Local state for optimistic updates (same pattern as useToggleLike)
  const [localLiked, setLocalLiked] = useState(initiallyLiked);

  // Sync local state when props change (e.g., after query refetch)
  useEffect(() => {
    setLocalLiked(initiallyLiked);
  }, [initiallyLiked]);

  const { toggleLikeAsync, isLoading } = useToggleLikeInList({ doctype });

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); // Prevent row click from firing
    e.preventDefault(); // Prevent any default button behavior

    // If not logged in, do nothing (button is disabled, but keep it defensive)
    if (candidateUserIds.length === 0) return;

    // Optimistic update (same pattern as detail page)
    const prev = localLiked;
    const next = !localLiked;
    setLocalLiked(next);

    try {
      await toggleLikeAsync({ name, nextLiked: next });
    } catch {
      // Roll back optimistic UI on failure
      setLocalLiked(prev);
    }
  };

  return (
    <button
      type="button"
      disabled={isLoading || candidateUserIds.length === 0}
      onClick={handleClick}
      className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      aria-label={localLiked ? "Unlike" : "Like"}
      aria-pressed={localLiked}
    >
      <Heart
        className={`h-5 w-5 transition-colors ${
          localLiked
            ? "fill-red-500 text-red-500"
            : "text-muted-foreground/60 hover:text-red-500"
        }`}
      />
    </button>
  );
}

